import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import type {
  Banner,
  Product,
  Service,
  Testimonial,
  Case,
  Category,
} from "@/lib/supabase";

import { ntProducts } from "@/mocks/newtechData";

// ── Banners ──────────────────────────────────────────────────────────────────
export function useBanners() {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("banners")
      .select("*")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) setBanners(data);

        setLoading(false);
      });
  }, []);

  return { banners, loading };
}

// ── Products ─────────────────────────────────────────────────────────────────
export function useProducts(featuredOnly = false) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let query = supabase
      .from("products")
      .select("*")
      .eq("visible", true)
      .order("display_order", { ascending: true });

    if (featuredOnly) {
      query = query.eq("featured", true);
    }

    query.then(({ data }) => {
      if (data && data.length > 0) {
        setProducts(data);
      } else {
        setProducts(ntProducts as Product[]);
      }

      setLoading(false);
    });
  }, [featuredOnly]);

  return { products, loading };
}

// ── Categories ────────────────────────────────────────────────────────────────
export function useCategories() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("categories")
      .select("*")
      .order("name", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setCategories(data);
        }

        setLoading(false);
      });
  }, []);

  return { categories, loading };
}

// ── Services ──────────────────────────────────────────────────────────────────
export function useServices() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("services")
      .select("*")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setServices(data);
        }

        setLoading(false);
      });
  }, []);

  return { services, loading };
}

// ── Testimonials ──────────────────────────────────────────────────────────────
export function useTestimonials() {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("testimonials")
      .select("*")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setTestimonials(data);
        }

        setLoading(false);
      });
  }, []);

  return { testimonials, loading };
}

// ── Cases ─────────────────────────────────────────────────────────────────────
export function useCases() {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("cases")
      .select("*")
      .eq("active", true)
      .order("display_order", { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setCases(data);
        }

        setLoading(false);
      });
  }, []);

  return { cases, loading };
}